import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
    ClipboardList, Plus, FileDown, ChevronRight, Loader2, ShieldCheck, FileText, Trash2,
} from 'lucide-react';
import { toast } from 'sonner';
import { studyFormsAPI } from '@/lib/api';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

export default function StudyFormsSection({ prospectId }) {
    const navigate = useNavigate();
    const [studies, setStudies] = useState([]);
    const [loading, setLoading] = useState(true);
    const [creating, setCreating] = useState(false);

    const load = async () => {
        try {
            const res = await studyFormsAPI.list(prospectId);
            setStudies(res.data.studies || []);
        } catch {
            // silencieux : pas bloquant pour le dossier
        } finally {
            setLoading(false);
        }
    };
    useEffect(() => { load(); /* eslint-disable-next-line */ }, [prospectId]);

    const handleNew = async () => {
        setCreating(true);
        try {
            const res = await studyFormsAPI.create(prospectId, {});
            navigate(`/prospects/${prospectId}/study/${res.data.id}`);
        } catch (e) {
            toast.error(e.response?.data?.detail || 'Erreur lors de la création du relevé');
            setCreating(false);
        }
    };

    const openPdf = async (studyId) => {
        try {
            const u = await studyFormsAPI.pdfObjectUrl(studyId);
            // Téléchargement direct via une ancre → évite le bloqueur de pop-up (window.open)
            const a = document.createElement('a');
            a.href = u;
            a.download = `releve-etude-${studyId}.pdf`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            setTimeout(() => URL.revokeObjectURL(u), 4000);
        }
        catch { toast.error('PDF indisponible'); }
    };

    const handleDelete = async (e, studyId) => {
        e.stopPropagation();
        if (!window.confirm('Supprimer définitivement ce relevé et tous ses fichiers ?')) return;
        try {
            await studyFormsAPI.remove(studyId);
            setStudies((prev) => prev.filter((s) => s.id !== studyId));
            toast.success('Relevé supprimé');
        } catch (err) {
            toast.error(err.response?.data?.detail || 'Suppression non autorisée');
        }
    };

    return (
        <Card className="border-indigo-200 bg-gradient-to-br from-indigo-50/60 via-white to-sky-50/40" data-testid="study-forms-section">
            <CardHeader className="pb-3">
                <CardTitle className="flex items-center justify-between gap-3 flex-wrap">
                    <span className="flex items-center gap-2 text-base sm:text-lg text-slate-900">
                        <ClipboardList className="w-5 h-5 text-indigo-600" />
                        Relevé d'étude sur place
                    </span>
                    <Button onClick={handleNew} disabled={creating} size="sm" className="bg-indigo-600 hover:bg-indigo-700 gap-1.5" data-testid="study-new-btn">
                        {creating ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                        Nouveau relevé
                    </Button>
                </CardTitle>
                <p className="text-xs text-slate-500">
                    Préparez et complétez votre étude directement dans le CRM. Tout est enregistré en continu (même hors-ligne) — zéro perte.
                </p>
            </CardHeader>
            <CardContent>
                {loading ? (
                    <div className="py-6 text-center text-slate-400 text-sm flex items-center justify-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" /> Chargement…
                    </div>
                ) : studies.length === 0 ? (
                    <div className="py-6 text-center text-slate-500">
                        <FileText className="w-9 h-9 mx-auto text-slate-300 mb-2" />
                        <p className="text-sm">Aucun relevé pour ce dossier.</p>
                        <p className="text-xs text-slate-400 mt-0.5">Cliquez sur « Nouveau relevé » pour démarrer.</p>
                    </div>
                ) : (
                    <div className="space-y-2" data-testid="study-list">
                        {studies.map((s) => (
                            <button key={s.id} type="button" onClick={() => navigate(`/prospects/${prospectId}/study/${s.id}`)}
                                className="w-full text-left rounded-lg border border-slate-200 bg-white hover:border-indigo-300 hover:bg-indigo-50/30 transition-colors p-3 flex items-center gap-3"
                                data-testid={`study-row-${s.id}`}>
                                <div className={`w-9 h-9 rounded-full flex items-center justify-center flex-shrink-0 ${s.status === 'validated' ? 'bg-emerald-100 text-emerald-600' : 'bg-amber-100 text-amber-600'}`}>
                                    {s.status === 'validated' ? <ShieldCheck className="w-4.5 h-4.5" /> : <ClipboardList className="w-4.5 h-4.5" />}
                                </div>
                                <div className="min-w-0 flex-1">
                                    <div className="text-sm font-medium text-slate-800 flex items-center gap-2">
                                        {s.status === 'validated' ? 'Relevé validé' : 'Brouillon en cours'}
                                        <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-500">{s.files_count} fichier{s.files_count > 1 ? 's' : ''}</span>
                                    </div>
                                    <div className="text-xs text-slate-500 mt-0.5">
                                        {s.created_by_name} · {s.created_at ? format(new Date(s.created_at), 'dd/MM/yy HH:mm', { locale: fr }) : ''}
                                    </div>
                                </div>
                                {s.has_pdf && (
                                    <span onClick={(e) => { e.stopPropagation(); openPdf(s.id); }} className="p-1.5 rounded hover:bg-slate-100 text-slate-500" title="Télécharger le PDF" data-testid={`study-pdf-${s.id}`}>
                                        <FileDown className="w-4 h-4" />
                                    </span>
                                )}
                                {s.can_edit && (
                                    <span onClick={(e) => handleDelete(e, s.id)} className="p-1.5 rounded hover:bg-red-50 text-slate-400 hover:text-red-600" title="Supprimer" data-testid={`study-del-${s.id}`}>
                                        <Trash2 className="w-4 h-4" />
                                    </span>
                                )}
                                <ChevronRight className="w-4 h-4 text-slate-400 flex-shrink-0" />
                            </button>
                        ))}
                    </div>
                )}
            </CardContent>
        </Card>
    );
}
